"use client";

/**
 * Field — labeled input wrapper. Use it when you have a single labeled control
 * (input/select/checkbox/textarea) inside a form.
 *
 * Optional `hint` appears below the label as small muted text. Optional `icon`
 * renders inline with the label.
 */
export interface FieldProps {
  label: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function Field({ label, hint, icon, children, className = "" }: FieldProps) {
  return (
    <div className={className}>
      <label className="flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-slate-300 mb-1.5">
        {icon}
        <span>{label}</span>
        {hint && (
          <span className="text-[10px] text-slate-500 font-normal">{hint}</span>
        )}
      </label>
      {children}
    </div>
  );
}

/**
 * Standard input className used across forms. Accent-aware focus ring via
 * --cc-accent-500 (defaults to teal). Note: radius is hardcoded in this class;
 * for full GAM Brand v2 skin awareness, consumers should layer
 * `style={{ borderRadius: 'var(--gam-radius-input, 0.5rem)' }}` on top.
 *
 * The Tailwind `rounded-lg` here is the fallback when no GAM skin is active —
 * matches v1 styling.
 */
export const inputCls =
  "w-full rounded-lg border border-slate-700/50 bg-slate-900/60 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none transition focus:border-[var(--cc-accent-500)]";

/**
 * Inline style helper for inputs that want full GAM Brand v2 awareness.
 * Pair with `inputCls` for class-based styling.
 *
 * @example
 * <input className={inputCls} style={gamInputStyle} />
 */
export const gamInputStyle: React.CSSProperties = {
  borderRadius: "var(--gam-radius-input, 0.5rem)",
  transitionTimingFunction: "var(--gam-spring-default, cubic-bezier(0.4, 0, 0.2, 1))",
};
